// The restaurant action list (res/GetresActionAllList) for the Captain, KOT,
// Packing and Waiter screens, in its OWN store slot: state.master.kotActions.
//
// It used to live in state.master.tableLists, the slot every list in the app
// shares. A live refresh that finished after the user had moved to another
// screen would then have overwritten that screen's data.
//
// Two ways it loads:
//   everything       from the main backend, exactly as before — on mount, on a
//                    manual refresh, and whenever the live connection says
//                    events may have been missed (connect, reconnect, tab wake);
//   only some orders from the realtime server, when a kotChanged hint names
//                    their request ids. Every row of a named request is
//                    replaced by what the server returns now; a request that
//                    returns nothing has left the list.
//
// Never two fetches at once: whatever arrives while one is running is collected
// and fetched right after, in order, so the list can never end up behind a
// change it already heard about.

import * as actionTypes from './actionTypes';
import axios from '../../axios-url';
import { Companyid } from '../../shared/Config';
import { REALTIME_URL, resolveBranchId, subscribe } from '../../POS/realtime/posRealtime';

const DEBOUNCE_MS = 150;        // a burst of hints becomes one fetch
const MAX_PARTIAL_IDS = 50;     // more than this in one go: just reload everything

const key = (id) => String(id).toLowerCase();

// Rows of the named requests are swapped for `rows`, in the place the request
// already had in the list; a request the list has not seen yet goes at the end.
export const mergeByRequest = (current, requestIds, rows) => {
    const asked = new Set(requestIds.map(key));
    const fresh = {};
    rows.forEach((r) => {
        const k = key(r.requestid);
        (fresh[k] = fresh[k] || []).push(r);
    });

    const next = [];
    const placed = new Set();
    (Array.isArray(current) ? current : []).forEach((r) => {
        const k = key(r.requestid);
        if (!asked.has(k)) { next.push(r); return; }
        if (placed.has(k)) return;
        placed.add(k);
        (fresh[k] || []).forEach((x) => next.push(x));
    });
    Object.keys(fresh).forEach((k) => {
        if (!placed.has(k)) fresh[k].forEach((x) => next.push(x));
    });
    return next;
};

let inFlight = false;
let pendingFull = false;
let pendingLoud = false;        // show the page loader for this load (a person asked for it)
const pendingIds = new Set();
let debounce = null;

const run = async (dispatch, getState) => {
    if (inFlight) return;
    if (!pendingFull && pendingIds.size === 0) return;

    const full = pendingFull || !REALTIME_URL || pendingIds.size > MAX_PARTIAL_IDS;
    const loud = pendingLoud;
    const ids = Array.from(pendingIds);
    pendingFull = false;
    pendingLoud = false;
    pendingIds.clear();

    inFlight = true;
    if (loud) dispatch({ type: actionTypes.KOT_ACTIONS_START });
    try {
        if (full) {
            const res = await axios.get('/master/api/res/GetresActionAllList?companyId=' + Companyid);
            dispatch({ type: actionTypes.KOT_ACTIONS_SET, rows: Array.isArray(res.data) ? res.data : [], loud });
        } else {
            const res = await fetch(REALTIME_URL + '/api/actions?companyId=' + Companyid + '&requestIds=' + ids.join(','));
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const rows = await res.json();
            dispatch({
                type: actionTypes.KOT_ACTIONS_SET,
                rows: mergeByRequest(getState().master.kotActions, ids, rows),
                loud,
            });
        }
    } catch (err) {
        if (!full) {
            // The realtime server could not answer: the main backend still can.
            pendingFull = true;
        } else if (loud) {
            dispatch({
                type: actionTypes.KOT_ACTIONS_FAIL,
                error: (err && err.response && err.response.data) || 'Table List Api Error',
            });
        }
    } finally {
        inFlight = false;
        if (pendingFull || pendingIds.size > 0) run(dispatch, getState);
    }
};

// Reload the whole list now. This is what a screen calls on mount, from its
// refresh button and after its own save.
export const loadKotActions = () => (dispatch, getState) => {
    pendingFull = true;
    pendingLoud = true;
    clearTimeout(debounce);
    run(dispatch, getState);
};

// Keep the list live while a screen is open. Returns the function that stops it:
//   componentDidMount()    { this._stopKotLive = this.props.startKotActionsLive(); }
//   componentWillUnmount() { this._stopKotLive(); }
// With no realtime server configured it does nothing, and the screen behaves as before.
export const startKotActionsLive = () => (dispatch, getState) => {
    if (!REALTIME_URL) return () => {};

    // requestIds: the orders to refresh, or null/empty for "reload everything".
    const schedule = (requestIds) => {
        if (!requestIds || requestIds.length === 0) pendingFull = true;
        else requestIds.forEach((id) => pendingIds.add(key(id)));
        clearTimeout(debounce);
        debounce = setTimeout(() => run(dispatch, getState), DEBOUNCE_MS);
    };

    // Tablets drop sockets silently when they sleep: reload on wake.
    const onVisibility = () => { if (!document.hidden) schedule(null); };
    document.addEventListener('visibilitychange', onVisibility);

    const unsubscribe = subscribe({
        companyId: Companyid,
        branchId: resolveBranchId(getState()),
        events: { kotChanged: (p) => schedule(p && p.requestIds) },
        onResync: () => schedule(null),
    });

    return () => {
        clearTimeout(debounce);
        document.removeEventListener('visibilitychange', onVisibility);
        unsubscribe();
    };
};
